/* eslint-disable @atlassian/relay/unused-fields */
import { useCallback } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { fetchQuery, graphql } from 'react-relay';
import { createDebouncedColumnLoader } from '@atlassian/jira-issue-table-column-picker/src/common/constants.tsx';
import type {
	EndCursor,
	OptionResponse,
	SelectOption,
	SelectValue,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { DataLoader } from '@atlassian/jira-jql-builder-basic-picker/src/common/utils/debouncer.tsx';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import type {
	useColumnLoaderQuery as UseColumnLoaderQuery,
	useColumnLoaderQuery$data as UseColumnLoaderQueryData,
} from '@atlassian/jira-relay/src/__generated__/useColumnLoaderQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

interface FetchColumnOptionsProps {
	cloudId: string;
	search: string;
	enableFullList: boolean;
	endCursor: EndCursor;
	issueKey: string;
}

const mapColumnPickerOptions = (data: UseColumnLoaderQueryData | undefined): OptionResponse => {
	const fieldSets = data?.jira?.fieldSetViewQueryByProject?.fieldSets;
	// Error
	if (!fieldSets) {
		return {
			options: [],
			totalCount: 0,
		};
	}
	const options: ReadonlyArray<SelectOption> =
		fieldSets?.edges?.map((edge) => ({
			optionType: 'option',
			cursor: edge?.cursor,
			value: edge?.node?.fieldSetId || '',
			label: edge?.node?.displayName || '',
			...(fg('jira_inline_field_config_gate') && {
				type: edge?.node?.type || '',
			}),
		})) || [];
	return {
		options,
		totalCount: fieldSets?.totalCount || null,
		endCursor: fieldSets?.pageInfo.endCursor || undefined,
	};
};

const fetchColumnOptions = async ({
	issueKey,
	cloudId,
	search,
	endCursor,
}: FetchColumnOptionsProps): Promise<OptionResponse> => {
	const environment = getRelayEnvironment();
	const cursor = endCursor;

	try {
		const data = await fetchQuery<UseColumnLoaderQuery>(
			environment,
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files, jira/relay/no-query
			graphql`
				query useColumnLoaderQuery(
					$cloudId: ID!
					$first: Int
					$after: String
					$filter: JiraIssueSearchFieldSetsFilter
					$issueKey: String!
				) {
					jira {
						fieldSetViewQueryByProject(cloudId: $cloudId, issueKey: $issueKey, projectId: null)
							@optIn(to: "JiraIssueSearch") {
							... on JiraFieldSetView {
								id
								fieldSets(first: $first, filter: $filter, after: $after) {
									totalCount
									pageInfo {
										endCursor
									}
									edges {
										cursor
										node {
											fieldSetId
											displayName
											type
											fieldType {
												displayName
											}
										}
									}
								}
							}
						}
					}
				}
			`,
			{
				cloudId,
				first: 25,
				after: cursor,
				issueKey,
				filter: {
					searchString: search,
					fieldSetSelectedState: 'ALL',
				},
			},
		).toPromise();
		return mapColumnPickerOptions(data);
	} catch (error) {
		const optionsResponse: OptionResponse = {
			options: [
				{
					optionType: 'error',
					error: new Error(`Error fetching field set view: ${error}`),
					onRetry: () => undefined,
				},
			],
			totalCount: 0,
		};
		return optionsResponse;
	}
};

interface UseColumnLoaderProps {
	issueKey: string;
	selectedColumns: SelectValue;
}

export const useColumnLoader = ({ issueKey }: UseColumnLoaderProps) => {
	const cloudId = useCloudId();

	const loadOptionsCallback: DataLoader = useCallback(
		async (search: string, enableFullList: boolean, endCursor: EndCursor) => {
			return fetchColumnOptions({
				issueKey,
				cloudId,
				search,
				enableFullList,
				endCursor,
			});
		},
		[cloudId, issueKey],
	);

	return createDebouncedColumnLoader(loadOptionsCallback);
};
